<!-- components/PasswordInput.vue -->
<template>
  <div class="password-input-wrapper">
    <div class="password-input-container">
      <input
        ref="hiddenInput"
        v-model="internalValue"
        type="password"
        maxlength="6"
        inputmode="numeric"
        pattern="\d*"
        class="hidden-input"
        @input="handleInput"
        @blur="$emit('onblur')"
      />
      <div class="password-squares" @click="focusInput">
        <div
          v-for="(digit, index) in displayedDigits"
          :key="index"
          class="password-square"
          :class="{ 'has-value': digit, 'error': hasError }"
        >
          <span class="digit">{{ digit ? '●' : '' }}</span>
          <span v-if="!digit && isActive && index === currentPos" class="cursor"></span>
        </div>
      </div>
    </div>
    <div v-if="error" class="error-message">{{ error }}</div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'

const props = defineProps({
  modelValue: {
    type: String,
    default: ''
  },
  error: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['update:modelValue', 'onblur'])

const internalValue = ref(props.modelValue)
const isActive = ref(false)
const hiddenInput = ref(null)

const displayedDigits = computed(() => {
  const digits = internalValue.value.split('')
  return Array(6).fill().map((_, i) => digits[i] || '')
})

const currentPos = computed(() => internalValue.value.length)

const hasError = computed(() => !!props.error)

watch(() => props.modelValue, (newVal) => {
  if (newVal !== internalValue.value) {
    internalValue.value = newVal
  }
})

const handleInput = (e) => {
  let value = e.target.value
  // 只允许数字
  value = value.replace(/\D/g, '').slice(0, 6)
  internalValue.value = value
  emit('update:modelValue', value)
}

const focusInput = () => {
  if (hiddenInput.value) {
    hiddenInput.value.focus()
    isActive.value = true
  }
}

const handleBlur = () => {
  isActive.value = false
  emit('onblur')
}

// 监听隐藏输入框的聚焦状态
if (hiddenInput.value) {
  hiddenInput.value.addEventListener('focus', () => { isActive.value = true })
  hiddenInput.value.addEventListener('blur', handleBlur)
}
</script>

<style scoped>
.password-input-wrapper {
  width: 100%;
}

.password-input-container {
  position: relative;
  width: 100%;
}

.hidden-input {
  position: absolute;
  opacity: 0;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  cursor: pointer;
  z-index: 1;
}

.password-squares {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  width: 100%;

  border-radius: 8px;
}

.password-square {
  flex: 1;
  aspect-ratio: 1 / 1;
  background: #f5f5f5;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: bold;
  position: relative;
  border: 1px solid #e0e0e0;
}

.password-square.has-value {
  background: #fff;
  border-color: #ccc;
}

.password-square.error {
  border-color: #ff4d4f;
  background: #fff2f0;
}

.digit {
  font-size: 28px;
  color: #333;
}

.cursor {
  width: 2px;
  height: 24px;
  background-color: #1677ff;
  animation: blink 1s step-end infinite;
}

@keyframes blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
}

.error-message {
  font-size: 12px;
  color: #ff4d4f;
  margin-top: 4px;
  padding-left: 8px;
}
</style>